import { faEye, faEyeSlash,faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormControl, FormHelperText, FormLabel, Input } from "@mui/joy";
import { IconButton, Typography, Button, Dialog, DialogContent, DialogTitle, DialogActions } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";

import "../../src/css/AttorneyForm.css";
import { changeUsFormatNumber } from "../components/Common";
import "../css/myestare.css";
import {
  checkEmail,
  checkUsername,
  secondaryUserSignUp,
  signup,
  initiateQR,
  verifyCode,
  userSignUpWith2FA
} from "../services/user-service";

export default function AttorneySignUp() {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(null);
  const [requires2FA, setRequires2FA] = useState(false);
  const navigate = useNavigate();
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    const urlToken = searchParams.get("token");
    const require2fa = searchParams.get("require2fa");

    if (urlToken) {
      setToken(urlToken);
      const needs2FA = require2fa === "true";
      setRequires2FA(needs2FA);

      setData((prevData) => ({
        ...prevData,
        role: "USER",
        jointAccount: true,
        accountType: needs2FA ? "primary" : "secondary",
      }));
    }
  }, [searchParams]);

  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    temporaryUsername: "",
    phoneNo: "",
    role: "ATTORNEY",
    jointAccount: false,
    accountType: "",
  });

  const [errors, setErrors] = useState({
    firstName: true,
    lastName: true,
    username: true,
    email: true,
    phoneNo: true,
    firstNameValidate: true,
    lastNameValidate: true,
    emailValidate: true,
    phoneValidate: true,
    password: true,
    passwordValidate: true,
    usernameValidate: true,
  });

  const validateAlphanumeric = (value) => !/^\d+$/.test(value);
  const validateEmail = (email) => /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(email);
  const validatePhoneNumber = (phoneNo) => /^(\+1\s?)?(\(?\d{3}\)?[\s.-]?)?\d{3}[\s.-]?\d{4}$/.test(phoneNo);
  const validatePassword = (password) => /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{10,}$/.test(password);
  const validateUsername = (username) => /^[a-z][a-z0-9_]*$/.test(username);

  const checkEmailExists = (email) => {
    return new Promise((resolve, reject) => {
      if (email !== "" && validateEmail(email)) {
        checkEmail(email)
          .then((res) => resolve(res))
          .catch((err) => {
            toast.error("Error!");
            reject(err);
          });
      } else {
        resolve(false);
      }
    });
  };

  const checkUsernameExists = (username) => {
    return new Promise((resolve, reject) => {
      if (username !== "") {
        checkUsername(username)
          .then((res) => resolve(res))
          .catch((err) => {
            toast.error("Error");
            reject(err);
          });
      } else {
        resolve(false);
      }
    });
  };

  const handleChanges = (event, property) => {
    const value = event.target.value;
    const errorStates = { ...errors };

    if (property === "phoneNo") {
      const formattedValue = changeUsFormatNumber(value);
      setData((prevData) => ({ ...prevData, [property]: formattedValue }));
      errorStates[property] = !!formattedValue;
    } else {
      setData((prevData) => ({ ...prevData, [property]: value }));
      errorStates[property] = !!value;
    }

    if (property === "email") errorStates.emailValidate = validateEmail(value);
    else if (property === "phoneNo") errorStates.phoneValidate = validatePhoneNumber(changeUsFormatNumber(value));
    else if (property === "username") errorStates.usernameValidate = validateUsername(value);
    else if (property === "password") errorStates.passwordValidate = validatePassword(value);
    else if (property === "firstName") errorStates.firstNameValidate = validateAlphanumeric(value);
    else if (property === "lastName") errorStates.lastNameValidate = value !== "" ? validateAlphanumeric(value) : true;

    setErrors(errorStates);
  };

  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const [validateExistsEmail, setValidateExistsEmail] = useState(false);
  const [validateExistsUsername, setValidateExistsUsername] = useState(false);

  const submitForm = async (event) => {
    event.preventDefault();
    const newErrors = {
      firstName: !!data.firstName,
      email: !!data.email,
      username: !!data.username,
      password: !!data.password,
      phoneNo: !!data.phoneNo,
      emailValidate: validateEmail(data.email),
      phoneValidate: validatePhoneNumber(changeUsFormatNumber(data.phoneNo)),
      firstNameValidate: validateAlphanumeric(data.firstName),
      lastNameValidate: data.lastName ? validateAlphanumeric(data.lastName) : true,
      passwordValidate: validatePassword(data.password),
      usernameValidate: validateUsername(data.username),
    };
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).includes(false);
    const { username, email } = data;
    if (!hasErrors) {
      try {
        const isEmailExist = await checkEmailExists(email);
        if (isEmailExist) {
          setValidateExistsEmail(isEmailExist);
          return;
        }
        const isUsernameExist = await checkUsernameExists(username);
        if (isUsernameExist) {
          setValidateExistsUsername(isUsernameExist);
          return;
        }
      } catch (err) {
        return;
      }

      if (token) {
        if (requires2FA) {
          toast.info("2FA setup required. Please scan the QR code.");
          await initiate2FA();
        } else {
          await attemptRegistrationWithToken();
        }
      } else {
        await registerNormalUser();
      }
    }
  };

  const attemptRegistrationWithToken = async () => {
    const registrationModel = { token: token };
    const formData = new FormData();
    formData.append("data", JSON.stringify(data));
    formData.append("request", JSON.stringify(registrationModel));

    secondaryUserSignUp(formData)
      .then((res) => {
        if (res) {
          toast.success("Registration Successful! Please Login.");
          navigate("/");
        }
      })
      .catch((err) => {
        if (err.response && err.response.status === 400) {
          let errorMessage = err.response.data;
          if (typeof errorMessage === 'object' && errorMessage !== null) {
            errorMessage = errorMessage.message || JSON.stringify(errorMessage);
          }

          if (errorMessage && errorMessage.includes("error:invalid_token")) {
            toast.error("Invalid token.");
          } else if (errorMessage && errorMessage.includes("error:invitation_accepted")) {
            toast.error("Invitation already accepted.");
          } else {
            toast.error("Bad request. Please try again.");
          }
        } else {
          toast.error("Something went wrong.");
        }
      });
  };

  const attemptRegistrationWith2FA = async () => {
    const registrationModel = { token: token };
    const formData = new FormData();
    formData.append("data", JSON.stringify(data));
    formData.append("request", JSON.stringify(registrationModel));

    userSignUpWith2FA(formData)
      .then((res) => {
        if (res) {
          toast.success("Registration Successful! Please Login.");
          navigate("/");
        }
      })
      .catch((err) => {
        if (err.response && err.response.status === 400) {
          let errorMessage = err.response.data;
          if (typeof errorMessage === 'object' && errorMessage !== null) {
            errorMessage = errorMessage.message || JSON.stringify(errorMessage);
          }

          if (errorMessage && errorMessage.includes("error:otp_verification_required")) {
            toast.error("Please complete 2FA verification first.");
          } else if (errorMessage && errorMessage.includes("error:invalid_token")) {
            toast.error("Invalid token.");
          } else if (errorMessage && errorMessage.includes("error:invitation_accepted")) {
            toast.error("Invitation already accepted.");
          } else {
            toast.error("Bad request. Please try again.");
          }
        } else {
          toast.error("Something went wrong.");
        }
      });
  };

  const initiate2FA = async () => {
    if (!data.username) {
      toast.error("Username is missing.");
      return;
    }
    try {
        const username = data.username;
        const payload = {username};
      const qrUrl = await initiateQR(payload);
      if (qrUrl) {
        setQrCodeUrl(qrUrl);
        setShowQRDialog(true);
      }
    } catch (err) {
      toast.error("Error initiating 2FA.");
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP code");
      return;
    }

    setIsVerifying(true);
    try {
      const payload = {
        username: data.username,
        otp: otpCode
      };

      const verified = await verifyCode(payload);

      if (verified) {
        toast.success("2FA verified successfully!");
        setShowQRDialog(false);
        setOtpCode("");

        await attemptRegistrationWith2FA();
      } else {
        toast.error("Invalid OTP code. Please try again.");
      }
    } catch (err) {
      toast.error("Error verifying OTP.");
    } finally {
      setIsVerifying(false);
    }
  };

  const closeQRDialog = () => {
    setShowQRDialog(false);
    setQrCodeUrl("");
    setOtpCode("");
  };

  const registerNormalUser = async () => {
    signup(data)
      .then(() => {
        toast.success("Registration Successful! Please Login.");
        navigate("/");
      })
      .catch(() => {
        toast.error("Registration failed.");
      });
  };

  return (
    <>
      <div className="attorney_container">
        <div className="attorney_registration_form">
          <div className="attorney_registration_form_heading">
            <h1 style={{ textAlign: "center", width: "100%" }}>
                {token ? (requires2FA ? "Primary User Registration" : "Secondary User Registration") : "Attorney Registration Form"}
            </h1>
          </div>
          <form className="attorney_form_main" onSubmit={submitForm}>
            <div className="attorney_form">
              <div className="attorney_form_left">
                {token === null && (
                  <p style={{ fontSize: "14px", color: "#d30000" }}>
                    Note: Please use same email where you received the invitation!
                  </p>
                )}

                <FormControl>
                  <FormLabel required>First Name</FormLabel>
                  <Input
                    className="input_mui_joy"
                    placeholder="Enter first name"
                    value={data.firstName}
                    onChange={(e) => handleChanges(e, "firstName")}
                  />
                  {(!errors.firstName || !errors.firstNameValidate) && (
                    <FormHelperText error>
                      <Typography variant="caption" color="error">
                        {!errors.firstName ? "First Name is required" : "Enter valid first name"}
                      </Typography>
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    className="input_mui_joy"
                    placeholder="Enter last name"
                    value={data.lastName}
                    onChange={(e) => handleChanges(e, "lastName")}
                  />
                  {!errors.lastNameValidate && (
                    <FormHelperText error>
                      <Typography variant="caption" color="error">Enter valid last name</Typography>
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel required>Email</FormLabel>
                  <Input
                    className="input_mui_joy"
                    placeholder="Enter your email"
                    value={data.email}
                    onChange={(e) => handleChanges(e, "email")}
                  />
                  {(!errors.email || !errors.emailValidate || validateExistsEmail) && (
                    <FormHelperText>
                      <Typography variant="caption" color="error">
                        {!errors.email ? "Email is required" : !errors.emailValidate ? "Enter a valid email" : "Email already exists"}
                      </Typography>
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel required>Username</FormLabel>
                  <Input
                    className="input_mui_joy"
                    placeholder="Enter username"
                    value={data.username}
                    onChange={(e) => handleChanges(e, "username")}
                  />
                  {(!errors.username || !errors.usernameValidate || validateExistsUsername) && (
                    <FormHelperText>
                      <Typography variant="caption" color="error">
                        {!errors.username ? "Username is required" : !errors.usernameValidate ? "Invalid format" : "Username already exists"}
                      </Typography>
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel required>Password</FormLabel>
                  <Input
                    placeholder="Enter your password"
                    value={data.password}
                    onChange={(e) => handleChanges(e, "password")}
                    type={showPassword ? "text" : "password"}
                    endDecorator={
                      <IconButton onClick={togglePasswordVisibility} edge="end" style={{ padding: 0 }}>
                        <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} style={{ fontSize: "16px", color: "black" }} />
                      </IconButton>
                    }
                  />
                  {(!errors.password || !errors.passwordValidate) && (
                    <FormHelperText>
                      <Typography variant="caption" color="error">
                        {!errors.password ? "Password is required" : "Password weak"}
                      </Typography>
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel required>Phone Number</FormLabel>
                  <Input
                    className="input_mui_joy"
                    placeholder="Enter phone number"
                    value={data.phoneNo}
                    onChange={(e) => handleChanges(e, "phoneNo")}
                  />
                  {(!errors.phoneNo || !errors.phoneValidate) && (
                    <FormHelperText>
                      <Typography variant="caption" color="error">
                        {!errors.phoneNo ? "Phone Number is required" : "Enter a valid phone number"}
                      </Typography>
                    </FormHelperText>
                  )}
                </FormControl>

                <div className="attorney_form_btn_main">
                  <div className="attorney_form_btn">
                    <button type="submit">Register</button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <Dialog open={showQRDialog} onClose={closeQRDialog} maxWidth="xs" fullWidth>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "24px",
            gap: "20px",
          }}
        >
          <p
            style={{
              fontSize: "20px",
              fontWeight: "500",
              margin: 0,
              textAlign: "center",
            }}
          >
            Scan this QR Code from Google Authenticator App
          </p>

          {qrCodeUrl ? (
            <div style={{ margin: "12px 0" }}>
              <QRCodeSVG value={qrCodeUrl} size={230} level="H" />
            </div>
          ) : (
            <div style={{ margin: "20px 0" }}>
              <p>Loading QR Code...</p>
            </div>
          )}

          <div>
            <Input
              type="text"
              value={otpCode}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,6}$/.test(value)) {
                  setOtpCode(value);
                }
              }}
              placeholder="Enter 6-digit Google Authenticator code"
              maxLength={6}
              style={{ textAlign: "center" }}
            />
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "10px",
              width: "100%",
              marginTop: "10px",
            }}
          >
            <Button
              className="back_button"
              onClick={closeQRDialog}
              type="button"
              fullWidth
            >
              Cancel
            </Button>

            <Button
              className="next_button"
              variant="contained"
              onClick={handleVerifyOTP}
              fullWidth
              disabled={isVerifying || otpCode.length !== 6}
            >
              {isVerifying ? "Verifying..." : "Verify & Register"}
              <FontAwesomeIcon className="arrow_icon" icon={faArrowRight} />
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}